import type { TFunction } from 'i18next'

import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import type { ToolbarStatusItem } from '~/components/common/filters'
import { formatDateDisplay } from '~/models/dateTime'
import type {
  SeriesKey,
  SeriesVisibility,
} from '~/pages/BalanceDemand/constants'

/**
 * 同時同量の需要・需要詳細で共有する、開閉式フィルターの「系列トグル」の要約と件数。
 * 両画面とも 基本表示 / 電圧階級別 / 分析補助 / 詳細 の 4 群を持つ (カードの行と同じ)。
 */

export type SeriesGroupKey = 'basic' | 'voltage' | 'analysis' | 'detail'

/** 親チェックボックス (基本表示は親を持たない)。 */
export type SeriesParentFlags = {
  voltage: boolean
  analysis: boolean
  detail: boolean
}

type SeriesGroupDef = {
  key: SeriesGroupKey
  labelKey: string
  keys: readonly SeriesKey[]
}

/** カードの行の並びと同じ。子の並びもカードのトグルの並びと同じ。 */
export const SERIES_GROUP_DEFS: readonly SeriesGroupDef[] = [
  {
    key: 'basic',
    labelKey: 'page:balanceDemand.filter.basicSeries',
    keys: ['demandPlan', 'demandActual', 'weather'],
  },
  {
    key: 'voltage',
    labelKey: 'page:balanceDemand.filter.voltageClass',
    keys: ['voltageExtraHigh', 'voltageHigh', 'voltageLow'],
  },
  {
    key: 'analysis',
    labelKey: 'page:balanceDemand.filter.analysis',
    keys: ['temperature', 'precipitation', 'procurementNet'],
  },
  {
    key: 'detail',
    labelKey: 'page:balanceDemand.filter.detail',
    keys: ['demandActualCorrected', 'dewPoint'],
  },
]

const SERIES_LABEL_KEYS: Record<SeriesKey, string> = {
  demandPlan: 'page:balanceDemand.filter.demandPlan',
  demandActual: 'page:balanceDemand.filter.demandActual',
  weather: 'page:balanceDemand.filter.weather',
  voltageExtraHigh: 'page:balanceDemand.filter.voltageExtraHigh',
  voltageHigh: 'page:balanceDemand.filter.voltageHigh',
  voltageLow: 'page:balanceDemand.filter.voltageLow',
  temperature: 'page:balanceDemand.filter.temperature',
  precipitation: 'page:balanceDemand.filter.precipitation',
  procurementNet: 'page:balanceDemand.filter.procurementNet',
  demandActualCorrected: 'page:balanceDemand.filter.demandActualCorrected',
  dewPoint: 'page:balanceDemand.filter.dewPoint',
}

/** 複数の値をつなぐ区切り (全画面共通。UI 辞書 toolbar-collapsible.md)。 */
export const SUMMARY_LIST_SEPARATOR = '・'
/** 1 つも選んでいない時の要約。 */
export const SUMMARY_NONE = '—'

export type SeriesGroupState = {
  def: SeriesGroupDef
  /** 既定 (全 ON) から変えているか。親が OFF の群は、子が既定でも変えた扱い。 */
  changed: boolean
  /** 今出している系列 (親が OFF なら 1 つも出していない)。 */
  shown: readonly SeriesKey[]
}

/**
 * 4 群それぞれの「既定から変えたか」と「今出している系列」。
 * 親 OFF の間は子の値を書き換えない設計なので (useFilterState)、出している系列は親でゲートして数える。
 */
export const getSeriesGroupStates = (
  series: SeriesVisibility,
  parents: SeriesParentFlags,
  defaultSeries: SeriesVisibility,
): SeriesGroupState[] =>
  SERIES_GROUP_DEFS.map((def) => {
    const parentOn = def.key === 'basic' || parents[def.key]
    const childrenChanged = def.keys.some(
      (key) => series[key] !== defaultSeries[key],
    )
    return {
      def,
      changed: !parentOn || childrenChanged,
      shown: parentOn ? def.keys.filter((key) => series[key]) : [],
    }
  })

/** 既定から変えた系列群の数 (フィルターボタンの件数に足す。群ごとに 1 件)。 */
export const countChangedSeriesGroups = (
  states: readonly SeriesGroupState[],
): number => states.filter((state) => state.changed).length

/** 選んだ値の要約。全部なら「すべて」、1 つも無ければ「—」、それ以外は「・」でつなぐ。 */
export const selectionSummaryValue = (
  selectedLabels: readonly string[],
  total: number,
  allLabel: string,
): string =>
  selectedLabels.length === 0
    ? SUMMARY_NONE
    : selectedLabels.length === total
      ? allLabel
      : selectedLabels.join(SUMMARY_LIST_SEPARATOR)

/**
 * 系列群の要約。見え方の設定なので、既定から変えた群だけ出す
 * (UI 辞書 toolbar-collapsible.md「要約」: 見え方の設定は変えた時だけ)。
 */
export const buildSeriesSummaryItems = (
  states: readonly SeriesGroupState[],
  t: TFunction,
): ToolbarStatusItem[] =>
  states
    .filter((state) => state.changed)
    .map(({ def, shown }) => ({
      key: `series-${def.key}`,
      label: t(def.labelKey),
      value: selectionSummaryValue(
        shown.map((key) => t(SERIES_LABEL_KEYS[key])),
        def.keys.length,
        t('common:filter.all'),
      ),
    }))

/** 要約の日付。日付ピッカーの閉じた表示と同じ `YYYY/MM/DD (曜)`。 */
export const formatSummaryDate = (date: Date, t: TFunction): string =>
  t('common:date.withWeekday', {
    date: formatDateDisplay(date),
    weekday: t(`common:date.calendar.weekday.${WEEKDAY_KEYS[date.getDay()]}`),
  })
